<template>
  <div class="relative flex flex-col justify-center w-full space-y-6">
    <div
      @click="showTooltip = !showTooltip"
      class="flex flex-row space-x-2 text-2xl font-bold text-bright-gray dark:text-white lg:text-4xl"
    >
      <h2>{{ item.questionDisplay }}</h2>
      <span v-if="item.required" class="text-ruby">*</span>
      <span class="cursor-pointer" v-if="item.tooltip.length"
        ><InformationCircleIcon class="w-8 h-8 text-gold"
      /></span>
    </div>
    <label for="" class="text-sm text-navada">{{ item.placeholder }} </label>
    <form
      class="w-full space-y-6"
      @submit.prevent="next"
      @keydown.right.prevent="next"
      @keydown.enter.prevent="next"
      @keydown.esc.prevent="$emit('close')"
    >
      <RadioGroup v-model="selected">
        <RadioGroupLabel class="sr-only">
          {{ item.questionDisplay }}
        </RadioGroupLabel>
        <div class="space-y-4">
          <RadioGroupOption
            v-focus
            as="template"
            v-for="(choice, idx) in item.choices"
            :key="idx"
            :value="idx"
            v-slot="{ active, checked }"
          >
            <div
              :class="[
                active ? '' : '',
                'relative md:w-full block rounded-lg border border-gray-300 bg-white dark:bg-[#1b1c20] shadow-sm px-6 py-4 cursor-pointer hover:border-gold sm:flex sm:justify-between focus:outline-none',
              ]"
            >
              <div class="flex items-center">
                <div class="text-sm">
                  <RadioGroupLabel
                    as="p"
                    class="text-xl font-bold text-gray-900 dark:text-white"
                  >
                    {{ choice }}
                  </RadioGroupLabel>
                </div>
              </div>

              <div
                :class="[
                  checked ? 'border-gold' : 'border-transparent',
                  'absolute -inset-px rounded-lg border-2 pointer-events-none',
                ]"
                aria-hidden="true"
              />
            </div>
          </RadioGroupOption>
        </div>
      </RadioGroup>

      <!-- Share Holders -->
      <ShareholderList
        @add="addPerson"
        @remove="removePerson"
        v-if="selected === 'yes'"
        :people="newExtra"
      />
      <!--# End shareholders -->

      <!-- Desktop action -->
      <div
        class="flex-col justify-between hidden w-full pt-4 md:flex md:w-full md:flex-row"
      >
        <div class="flex flex-row space-x-4">
          <button
            type="button"
            @click="next"
            class="inline-flex items-center justify-center flex-shrink-0 min-w-0 px-8 py-2 text-sm font-bold text-center text-black rounded-full bg-gold-alt focus:outline-none"
          >
            <span v-if="!isLoading">{{ t("input.continue") }}</span>
            <span v-else class="flex flex-row space-x-4"
              ><span>{{ t("input.saving") }}</span>
              <Spinner text-color="text-black"
            /></span>
          </button>
          <button
            type="submit"
            class="inline-flex items-center justify-center px-8 py-2 space-x-1 text-sm font-bold text-white rounded-full focus:outline-none"
          >
            <span class="text-navada">press</span>
            <span class="text-black dark:text-white">{{
              t("input.enter")
            }}</span>
            <span>
              <ReturnIcon />
            </span>
          </button>
        </div>
        <button
          v-if="!item.required"
          @click="$emit('skip', item, selected)"
          class="inline-flex items-center justify-center px-8 py-2 space-x-1 text-sm font-bold text-white rounded-full focus:outline-none"
        >
          <span class="text-black dark:text-white">{{ t("input.skip") }}</span>
          <SkipIcon />
        </button>
      </div>
      <!-- Mobile action -->
      <div class="flex flex-col w-full pt-4 space-y-4 md:hidden">
        <div class="flex flex-row w-full">
          <button
            type="button"
            @click="next"
            class="inline-flex items-center justify-center flex-shrink-0 w-full min-w-0 px-8 py-2 text-sm font-bold text-center text-black rounded-full bg-gold-alt focus:outline-none"
          >
            <span v-if="!isLoading">{{ t("input.continue") }}</span>
            <span v-else class="flex flex-row space-x-4"
              ><span>{{ t("input.saving") }}</span>
              <Spinner text-color="text-black"
            /></span>
          </button>
        </div>
        <div class="flex flex-row justify-end">
          <button
            v-if="!item.required"
            @click="$emit('skip', item, selected)"
            class="inline-flex items-center justify-center py-2 space-x-1 text-sm font-bold text-white rounded-full focus:outline-none"
          >
            <span class="text-black dark:text-white">{{
              t("input.skip")
            }}</span>
            <SkipIcon />
          </button>
        </div>
      </div>
    </form>

    <!-- Tooltip -->
    <tooltip-modal
      :show="showTooltip && item.tooltip.length"
      :tooltip="item.tooltip"
      @close="showTooltip = !showTooltip"
    />
    <!-- End tooltip -->

    <!-- Error -->
    <error-modal
      :show="showError"
      :error="error"
      @close="
        showError = !showError;
        error = '';
      "
    />
    <!-- End Error -->
  </div>
</template>

<script>
import { computed, reactive, ref, toRefs } from "vue";
import {
  RadioGroup,
  RadioGroupDescription,
  RadioGroupLabel,
  RadioGroupOption,
} from "@headlessui/vue";
import Spinner from "../Spinner";
import { useI18n } from "vue-i18n";
import {
  ExclamationCircleIcon,
  InformationCircleIcon,
} from "@heroicons/vue/solid";
import ReturnIcon from "../icons/ReturnIcon";
import SkipIcon from "../icons/SkipIcon";
import Shareholder from "./Shareholder";
import ShareholderList from "./ShareholderList";
import { v4 as uuidv4 } from "uuid";

export default {
  name: "Shareholders",
  components: {
    ShareholderList,
    Shareholder,
    SkipIcon,
    ReturnIcon,
    InformationCircleIcon,
    ExclamationCircleIcon,
    Spinner,
    RadioGroup,
    RadioGroupDescription,
    RadioGroupLabel,
    RadioGroupOption,
  },
  props: {
    isLoading: {
      type: Boolean,
      default: false,
    },
    item: {
      type: Object,
      required: true,
    },
  },
  setup(props, { emit }) {
    const { t } = useI18n();
    const answer = computed(() =>
      props.item.answer ? props.item.answer.answer?.value : "no"
    );
    const extra = computed(() => props.item.answer?.answer?.extra ?? []);
    const newExtra = ref(extra.value);
    const state = reactive({
      showTooltip: false,
      showError: false,
      error: "",
      selected: answer.value ? answer.value : "",
    });
    const next = () => {
      if (
        props.item.required &&
        (state.selected === null ||
          state.selected === "" ||
          state.selected.length === 0)
      ) {
        state.showError = true;
        state.error = t("input.required");
        return;
      }

      if (state.selected === "yes" && newExtra.value.length === 0) {
        state.showError = true;
        state.error = t("question.required");
        return;
      }

      if (state.selected === "no") {
        if (props.item.answer) {
          props.item.answer.answer.extra = [];
        }
        newExtra.value = []
      }

      emit("next", props.item, {
        value: state.selected,
        extra: newExtra.value,
      });
    };

    const addPerson = () => {
      newExtra.value.push({
        id: uuidv4(),
        name: "",
        dob: "",
        address: "",
        share: 0,
      });
    };

    const removePerson = (person) => {
      newExtra.value = newExtra.value.filter((item) => item.id !== person.id);
    };
    return {
      t,
      next,
      newExtra,
      extra,
      addPerson,
      removePerson,
      ...toRefs(state),
    };
  },
};
</script>
